/* File: src/contexts/game/gameSetup.js */
/* File: src/contexts/game/gameSetup.js */
import { useCallback } from 'react';
import { auth } from '../../firebaseConfig'; // Import Firebase auth instance

// Import the frontend wrapper function for the backend call
import { generateSequenceHttp } from './gameCloudFunctions';

// Import helpers (assuming they are correctly defined and exported in the frontend version)
// Frontend version might only have formatters etc. - calculateSessionParameters typically runs backend-side.
// import { calculateSessionParameters } from '../gameHelpers';

/**
 * Factory function returning an object containing the *logic* for setting up a game.
 * These are plain functions, NOT wrapped in useCallback here; the wrapping happens in GameContext.
 * Handles starting standard games, playground games, challenge attempts, and retries.
 *
 * @param {object} params - Dependencies required for setup actions.
 * @param {object|null} params.activeProfile - The currently active user profile object.
 * @param {object} params.settings - The user's application settings (contains selectedVoiceId).
 * @param {boolean} params.saveToProfileState - Current value of the saveToProfile state.
 * @param {object} params.challengeState - Current challenge state { isActive, targetNBack, wins }.
 * @param {boolean} params.isPlaygroundSessionState - Current value of isPlaygroundSession state.
 * @param {Function} params.setGameState - Setter for game state.
 * @param {Function} params.setNBack - Setter for standard n-back state.
 * @param {Function} params.setDifficulty - Setter for standard difficulty state.
 * @param {Function} params.setSequence - Setter for game sequence state.
 * @param {Function} params.setCurrentTrial - Setter for current trial index state.
 * @param {Function} params.setTotalTrials - Setter for total trials state.
 * @param {Function} params.setGameResults - Setter for game results state.
 * @param {Function} params.setFeedbackMessage - Setter for feedback message state.
 * @param {Function} params.setIsPlaygroundSession - Setter for playground flag.
 * @param {Function} params.setSaveToProfile - Setter for the save-to-profile flag state.
 * @param {Function} params.setPlaygroundSettings - Setter for playground settings.
 * @param {Function} params.startCountdown - Action to initiate the countdown.
 * @param {Function} params.setChallengeState - Setter for challenge state.
 * @param {Function} params.setConsecutiveSuccesses - Setter for standard consecutive successes state.
 * @param {Function} params.setShowCountdown - Setter for showing/hiding countdown UI.
 * @param {Function} params.setIsCountdownVisuallyComplete - Setter for countdown animation flag.
 * @param {React.MutableRefObject<NodeJS.Timeout|null>} params.timerRef - Ref for the game timer.
 * @param {React.MutableRefObject<boolean>} params.isEndingRef - Ref flag to prevent concurrent endGame executions.
 * @param {React.MutableRefObject<Array<number>>} params.visualResponsesRef - Ref for visual responses.
 * @param {React.MutableRefObject<Array<number>>} params.audioResponsesRef - Ref for audio responses.
 * @param {React.MutableRefObject<Array<object>>} params.visualMatchesRef - Ref for storing calculated visual matches.
 * @param {React.MutableRefObject<Array<object>>} params.audioMatchesRef - Ref for storing calculated audio matches.
 * @param {React.MutableRefObject<object|null>} params.sessionSettingsRef - Ref for storing generated session parameters.
 * @param {React.MutableRefObject<Array<object>>} params.replayEventsRef - Ref for replay event log.
 * @param {React.MutableRefObject<number|null>} params.sessionStartTimeRef - Ref for session start timestamp.
 * @param {React.MutableRefObject<Function|null>} params.onNavigateRef - Ref holding the navigation function.
 * @param {React.MutableRefObject<string>} params.latestGameStateRef - Ref holding the latest gameState.
 * @param {object} params.audioManager - Instance of the audio manager.
 * @returns {object} Object containing setup action logic functions: { startGameLogic, startPlaygroundGameLogic, cancelPreparationLogic }.
 */
export function createGameSetupActions({
    activeProfile, settings, // <<< 'settings' is now received and used
    saveToProfileState, challengeState, isPlaygroundSessionState,
    setGameState, setNBack, setDifficulty, setSequence, setCurrentTrial,
    setTotalTrials, setGameResults, setFeedbackMessage, setIsPlaygroundSession,
    setSaveToProfile, setPlaygroundSettings, startCountdown, setChallengeState,
    setConsecutiveSuccesses, setShowCountdown, setIsCountdownVisuallyComplete,
    timerRef, isEndingRef, visualResponsesRef, audioResponsesRef, visualMatchesRef,
    audioMatchesRef, sessionSettingsRef, replayEventsRef, sessionStartTimeRef,
    onNavigateRef, latestGameStateRef, audioManager
}) {

    /**
     * Core logic for starting a game session (standard, challenge, override).
     * Fetches sequence from backend, resets state, and triggers countdown.
     * @param {object} [options={}] - Optional settings overrides.
     * @param {boolean} [options.isChallengeAttempt] - Flag indicating a challenge attempt.
     * @param {number} [options.nBack] - N-Back level for challenge attempt.
     * @param {number} [options.difficulty] - Difficulty for challenge attempt (usually ignored/set to max).
     * @param {number} [options.overrideNBack] - Specific N-Back level for retry/override.
     * @param {number} [options.overrideDifficulty] - Specific difficulty level for retry/override.
     */
    const startGameLogic = async (options = {}) => {
        const functionStartTime = Date.now(); 
        console.log(`[startGameLogic ${functionStartTime}] Initiated with options:`, JSON.stringify(options));

        if (!auth.currentUser) {
            console.error(`[startGameLogic ${functionStartTime}] Aborted: User is not authenticated.`);
            setFeedbackMessage("Authentication error. Please log in again.");
            setGameState('idle'); setShowCountdown(false); return;
        }
        const currentState = latestGameStateRef.current;
        if (['preparing', 'playing', 'paused'].includes(currentState)) {
             console.warn(`[startGameLogic ${functionStartTime}] Aborted: Game already in progress or preparing (state: ${currentState}).`);
             return;
        }
        
        console.log(`[startGameLogic ${functionStartTime}] Current state is ${currentState}. Resetting state for new game...`);
        setGameState('preparing'); 
        setGameResults(null); setFeedbackMessage(''); setCurrentTrial(-1);
        setSequence([]); setTotalTrials(0); setIsPlaygroundSession(false);
        setPlaygroundSettings(null); 
        setShowCountdown(false); setIsCountdownVisuallyComplete(false);
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
        isEndingRef.current = false; visualResponsesRef.current = []; audioResponsesRef.current = [];
        visualMatchesRef.current = []; audioMatchesRef.current = []; sessionSettingsRef.current = null;
        replayEventsRef.current = []; sessionStartTimeRef.current = null;
        
        let nBackToUse = null; 
        let difficultyToUse = null;
        let isChallengeAttemptFlag = false;
        let sessionType = 'UNKNOWN'; 

        if (options.isChallengeAttempt === true && typeof options.nBack === 'number' && options.nBack >= 1) {
            sessionType = 'CHALLENGE (Explicit Options)';
            console.log(`[startGameLogic ${functionStartTime} - Param Det] Path taken: ${sessionType}`);
            nBackToUse = options.nBack;
            difficultyToUse = 0.50; // Challenge difficulty is N.50
            isChallengeAttemptFlag = true;
            setChallengeState({ isActive: true, targetNBack: nBackToUse, wins: 0 });
            setSaveToProfile(true);

        } else if (options.overrideNBack !== undefined && options.overrideDifficulty !== undefined) {
            sessionType = 'OVERRIDE';
            console.log(`[startGameLogic ${functionStartTime} - Param Det] Path taken: ${sessionType}`);
            nBackToUse = options.overrideNBack;
            difficultyToUse = Math.max(0, Math.min(0.99, options.overrideDifficulty)); 
            isChallengeAttemptFlag = false;
            setChallengeState({ isActive: false, targetNBack: null, wins: 0 });
            setSaveToProfile(true); 

        } else if (activeProfile) {
            sessionType = 'STANDARD (Profile)';
            console.log(`[startGameLogic ${functionStartTime} - Param Det] Path taken: ${sessionType}`);
            nBackToUse = activeProfile.currentNBack ?? 2;
            difficultyToUse = Math.max(0, Math.min(0.99, activeProfile.currentDifficulty ?? 0.0));
            isChallengeAttemptFlag = false; 
             if(challengeState.isActive) {
                  console.warn(`[startGameLogic ${functionStartTime}] Resetting active challenge state for standard game start.`);
                  setChallengeState({ isActive: false, targetNBack: null, wins: 0 });
             }
            setSaveToProfile(saveToProfileState); 

        } else {
            sessionType = 'ERROR (No Profile/Challenge/Override)';
            console.error(`[startGameLogic ${functionStartTime} - Param Det] Critical error: ${sessionType}. Cannot determine parameters.`);
            setFeedbackMessage("Internal error: Could not determine game level. Profile missing?");
            setGameState('idle'); 
             if (options.isChallengeAttempt === true) {
                  setChallengeState({ isActive: false, targetNBack: null, wins: 0 });
             }
            return;
        }
        
        console.log(`[startGameLogic ${functionStartTime} - Param Det] FINAL PARAMS: Type=${sessionType}, N=${nBackToUse}, Diff=${difficultyToUse}, isChallenge=${isChallengeAttemptFlag}`);
         if (nBackToUse === null || difficultyToUse === null) {
              console.error(`[startGameLogic ${functionStartTime}] CRITICAL INTERNAL ERROR: Parameters nBackToUse or difficultyToUse are null after determination block! Aborting.`);
              setFeedbackMessage("Internal error: Failed to set game parameters correctly.");
              setGameState('idle'); 
               if (isChallengeAttemptFlag) {
                    setChallengeState({ isActive: false, targetNBack: null, wins: 0 });
               }
              return;
         }
        
        const saveFlagToLog = saveToProfileState; 
        console.log(`[startGameLogic ${functionStartTime}] Setting context state: N=${nBackToUse}, Difficulty=${difficultyToUse}, Save=${saveFlagToLog}`);
        setNBack(nBackToUse);
        setDifficulty(difficultyToUse); 
        if (sessionType !== 'STANDARD (Profile)') {
             console.log(`[startGameLogic ${functionStartTime}] Resetting consecutive successes because session type is ${sessionType}.`);
             setConsecutiveSuccesses(0);
        } else if (activeProfile) {
             const profileStreak = activeProfile.consecutiveSuccesses || 0;
             console.log(`[startGameLogic ${functionStartTime}] Syncing consecutive successes from profile for STANDARD game: ${profileStreak}.`);
             setConsecutiveSuccesses(profileStreak);
        }
        
        const setupTimestamp = Date.now();
        replayEventsRef.current.push({
            type: 'gameSetup',
            timestamp: setupTimestamp,
            nBack: nBackToUse,
            difficulty: difficultyToUse, 
            isChallenge: isChallengeAttemptFlag,
            isPlayground: false, 
            isOverride: sessionType === 'OVERRIDE',
            profileId: activeProfile?.id || null
        });
        
        console.log(`[startGameLogic ${functionStartTime}] Preparing to call generateSequenceHttp. N=${nBackToUse}, D(Precise)=${difficultyToUse}, isChallenge=${isChallengeAttemptFlag}`);
        try {
            const difficultySeedForBackend = difficultyToUse >= 0.99 ? 1.0 : (difficultyToUse / 0.99);
            console.log(`[startGameLogic ${functionStartTime}] Calculated difficultySeedForBackend: ${difficultySeedForBackend} (from difficultyToUse: ${difficultyToUse})`);

            // --- MODIFICATION: Derive languageCode from settings ---
            let languageCodeForBackend = 'en'; // Default
            if (settings && settings.selectedVoiceId) { 
                const parts = settings.selectedVoiceId.split('-'); 
                if (parts.length >= 2) {
                    languageCodeForBackend = `${parts[0]}-${parts[1]}`; 
                }
            }
            console.log(`[startGameLogic ${functionStartTime}] Using languageCodeForBackend: ${languageCodeForBackend}`);
            // --- END MODIFICATION ---

            const backendCallStartTime = Date.now();
            const payload = { 
                nBack: nBackToUse,
                difficulty: difficultySeedForBackend, 
                isPlayground: false, 
                isChallengeAttempt: isChallengeAttemptFlag, 
                profileId: activeProfile?.id || null,
                modality: options.modality || 'dual', // Pass modality if provided, else default
                overrideTrials: options.overrideTrials || null, // Pass override if provided
                language: languageCodeForBackend // <<< ADD language to payload
            };
            console.log(`[startGameLogic ${functionStartTime}] Calling generateSequenceHttp with payload:`, JSON.stringify(payload));
            const response = await generateSequenceHttp(payload);
            const backendCallEndTime = Date.now();
            console.log(`[startGameLogic ${functionStartTime}] Backend call generateSequenceHttp took ${backendCallEndTime - backendCallStartTime} ms.`);

            if (!response || !response.sequence || !response.parameters) {
                throw new Error('Invalid sequence response structure from backend.');
            }
             if (!Array.isArray(response.sequence)) {
                  throw new Error('Backend returned non-array sequence.');
             }
             if (typeof response.parameters?.totalTrials !== 'number' || response.parameters.totalTrials <= 0) {
                  throw new Error(`Backend returned invalid totalTrials: ${response.parameters?.totalTrials}`);
             }
            console.log(`[startGameLogic ${functionStartTime}] Sequence received from backend. Trials: ${response.parameters.totalTrials}, Seq Length: ${response.sequence.length}`);

            sessionSettingsRef.current = {
                ...response.parameters, 
                nBack: nBackToUse, 
                difficulty: difficultyToUse, 
                isPlayground: false,
                isChallengeAttempt: isChallengeAttemptFlag, 
                showReport: true, 
                generatedSequence: response.sequence 
            };
            console.log(`[startGameLogic ${functionStartTime}] sessionSettingsRef updated:`, JSON.stringify(sessionSettingsRef.current));

            setSequence(response.sequence);
            setTotalTrials(response.parameters.totalTrials);
            visualMatchesRef.current = response.visualMatches || []; 
            audioMatchesRef.current = response.audioMatches || [];

            replayEventsRef.current.push({
                type: 'sequenceGenerated',
                timestamp: Date.now(),
                sequenceLength: response.sequence.length,
                totalTrials: response.parameters.totalTrials,
            });
            
            console.log(`[startGameLogic ${functionStartTime}] Setup complete. Calling startCountdown...`);
            startCountdown(); 

        } catch (error) {
             const errorTime = Date.now();
             const setupDurationOnError = errorTime - setupTimestamp; 
             console.error(`[startGameLogic ${functionStartTime}] Error during backend call or processing (occurred after ${setupDurationOnError} ms from setup log):`, error);
             setFeedbackMessage(`Error starting game: ${error.message}. Please try again.`);
             setGameState('idle'); 
             setShowCountdown(false);
             setSequence([]); setTotalTrials(0); sessionSettingsRef.current = null;
             visualMatchesRef.current = []; audioMatchesRef.current = []; replayEventsRef.current = [];
             if(isChallengeAttemptFlag) {
                 console.log(`[startGameLogic ${functionStartTime} - Error Handling] Resetting challenge state due to error.`);
                 setChallengeState({ isActive: false, targetNBack: null, wins: 0 });
             }
        } finally {
             const functionEndTime = Date.now();
             console.log(`[startGameLogic ${functionStartTime}] Execution finished. Total time: ${functionEndTime - functionStartTime} ms.`);
        }
    };

    const startPlaygroundGameLogic = async (settingsFromPlayground) => { 
        const functionStartTime = Date.now();
        console.debug(`[startPlaygroundGameLogic ${functionStartTime}] Initiated with settings:`, settingsFromPlayground);

        if (!settingsFromPlayground || typeof settingsFromPlayground.nBack !== 'number' || typeof settingsFromPlayground.difficulty !== 'number') {
            console.error(`[startPlaygroundGameLogic ${functionStartTime}] Aborted: Invalid playground settings provided.`);
            setFeedbackMessage("Invalid playground settings."); setGameState('idle'); return;
        }
         const currentState = latestGameStateRef.current;
         if (['preparing', 'playing', 'paused'].includes(currentState)) {
             console.warn(`[startPlaygroundGameLogic ${functionStartTime}] Aborted: Game already in progress or preparing (state: ${currentState}).`);
             return;
         }

        console.log(`[startPlaygroundGameLogic ${functionStartTime}] Current state is ${currentState}. Resetting state for playground...`);
        setGameState('preparing'); 
        setGameResults(null); setFeedbackMessage(''); setCurrentTrial(-1);
        setSequence([]); setTotalTrials(0); setIsPlaygroundSession(true); 
        setPlaygroundSettings(settingsFromPlayground); setSaveToProfile(false); 
        setShowCountdown(false); setIsCountdownVisuallyComplete(false);
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
        isEndingRef.current = false; visualResponsesRef.current = []; audioResponsesRef.current = [];
        visualMatchesRef.current = []; audioMatchesRef.current = []; sessionSettingsRef.current = null;
        replayEventsRef.current = []; sessionStartTimeRef.current = null;
        setChallengeState({ isActive: false, targetNBack: null, wins: 0 }); 

        const nBackToUse = settingsFromPlayground.nBack;
        const difficultyToUse = Math.max(0, Math.min(0.99, settingsFromPlayground.difficulty)); 
        const totalTrialsToUse = settingsFromPlayground.trials || 20; 
        const showReport = settingsFromPlayground.showReport !== false; 
        const customIsi = settingsFromPlayground.isi; 
        const customStimDuration = settingsFromPlayground.stimDuration; 
        const modalityToUse = settingsFromPlayground.modality || 'dual'; // Get modality from playground settings

        console.log(`[startPlaygroundGameLogic ${functionStartTime}] Setting context state: N=${nBackToUse}, Difficulty=${difficultyToUse}`);
        setNBack(nBackToUse);
        setDifficulty(difficultyToUse); 
        setConsecutiveSuccesses(0); 

        const setupTimestamp = Date.now();
        replayEventsRef.current.push({
            type: 'gameSetup', timestamp: setupTimestamp, nBack: nBackToUse, difficulty: difficultyToUse,
            isChallenge: false, isPlayground: true, settings: settingsFromPlayground, profileId: activeProfile?.id || null
        });

        console.log(`[startPlaygroundGameLogic ${functionStartTime}] Preparing to call generateSequenceHttp for playground...`);
        try {
             const difficultySeedForBackend = difficultyToUse >= 0.99 ? 1.0 : (difficultyToUse / 0.99); 
             console.log(`[startPlaygroundGameLogic ${functionStartTime}] Calculated difficultySeedForBackend: ${difficultySeedForBackend}`);

             const playgroundParams = {
                trials: totalTrialsToUse,
                ...(typeof customIsi === 'number' && { isi: customIsi }), 
                ...(typeof customStimDuration === 'number' && { stimDuration: customStimDuration }) 
            };
            console.log(`[startPlaygroundGameLogic ${functionStartTime}] Playground params for backend:`, playgroundParams);

            // --- MODIFICATION: Derive languageCode for playground game from global settings ---
            let languageCodeForBackend = 'en'; // Default for playground
            if (settings && settings.selectedVoiceId) { // Use the global app settings for voice
                const parts = settings.selectedVoiceId.split('-');
                if (parts.length >= 2) {
                    languageCodeForBackend = `${parts[0]}-${parts[1]}`;
                }
            }
            console.log(`[startPlaygroundGameLogic ${functionStartTime}] Using languageCodeForBackend: ${languageCodeForBackend} for playground`);
            // --- END MODIFICATION ---

             const backendCallStartTime = Date.now();
             const payload = { 
                 nBack: nBackToUse,
                 difficulty: difficultySeedForBackend, 
                 isPlayground: true,
                 isChallengeAttempt: false, 
                 profileId: activeProfile?.id || null, 
                 playgroundParams: playgroundParams,
                 modality: modalityToUse, // Pass modality for playground
                 // overrideTrials is implicitly handled by playgroundParams.trials if needed by backend
                 language: languageCodeForBackend // <<< ADD language to payload
             };
             console.log(`[startPlaygroundGameLogic ${functionStartTime}] Calling generateSequenceHttp with payload:`, JSON.stringify(payload));
             const response = await generateSequenceHttp(payload);
            const backendCallEndTime = Date.now();
            console.log(`[startPlaygroundGameLogic ${functionStartTime}] Backend call generateSequenceHttp took ${backendCallEndTime - backendCallStartTime} ms.`);

             if (!response || !response.sequence || !response.parameters) { throw new Error('Invalid sequence response from backend for playground.'); }
             if (!Array.isArray(response.sequence)) { throw new Error('Backend returned non-array sequence for playground.'); }
             if (typeof response.parameters?.totalTrials !== 'number' || response.parameters.totalTrials <= 0) { throw new Error(`Backend returned invalid totalTrials for playground: ${response.parameters?.totalTrials}`); }
             console.log(`[startPlaygroundGameLogic ${functionStartTime}] Sequence received. Trials: ${response.parameters.totalTrials}, Seq Length: ${response.sequence.length}`);

             sessionSettingsRef.current = {
                  ...response.parameters, 
                  nBack: nBackToUse,
                  difficulty: difficultyToUse, 
                  isPlayground: true,
                  isChallengeAttempt: false,
                  showReport: showReport, 
                  generatedSequence: response.sequence
             };
             console.log(`[startPlaygroundGameLogic ${functionStartTime}] sessionSettingsRef updated:`, JSON.stringify(sessionSettingsRef.current));

             setSequence(response.sequence);
             setTotalTrials(response.parameters.totalTrials);
             visualMatchesRef.current = response.visualMatches || [];
             audioMatchesRef.current = response.audioMatches || [];

             replayEventsRef.current.push({ type: 'sequenceGenerated', timestamp: Date.now(), sequenceLength: response.sequence.length, totalTrials: response.parameters.totalTrials });
             
             console.log(`[startPlaygroundGameLogic ${functionStartTime}] Setup complete. Calling startCountdown...`);
             startCountdown();

        } catch (error) {
             const errorTime = Date.now();
             const setupDurationOnError = errorTime - setupTimestamp;
             console.error(`[startPlaygroundGameLogic ${functionStartTime}] Error during backend call or processing (occurred after ${setupDurationOnError} ms from setup log):`, error);
             setFeedbackMessage(`Error starting playground: ${error.message}`);
             setGameState('idle'); setShowCountdown(false);
             setSequence([]); setTotalTrials(0); sessionSettingsRef.current = null;
             visualMatchesRef.current = []; audioMatchesRef.current = []; replayEventsRef.current = [];
             setIsPlaygroundSession(false); setPlaygroundSettings(null); 
        } finally {
             const functionEndTime = Date.now();
             console.log(`[startPlaygroundGameLogic ${functionStartTime}] Execution finished. Total time: ${functionEndTime - functionStartTime} ms.`);
        }
    };

    const cancelPreparationLogic = () => {
        const currentState = latestGameStateRef.current;
        console.debug(`[cancelPreparationLogic] Called with state: ${currentState}`);
        if (currentState === 'preparing') {
            console.log('[cancelPreparationLogic] Cancelling preparation...');
            setGameState('idle');
            setShowCountdown(false); setIsCountdownVisuallyComplete(false); setFeedbackMessage('');
            setSequence([]); setTotalTrials(0); sessionSettingsRef.current = null;
            visualMatchesRef.current = []; audioMatchesRef.current = []; replayEventsRef.current = [];
            if (isPlaygroundSessionState) {
                console.log('[cancelPreparationLogic] Resetting playground state.');
                setIsPlaygroundSession(false); setPlaygroundSettings(null);
            }
            const wasChallengeSetup = sessionSettingsRef.current?.isChallengeAttempt || challengeState.isActive;
            if (wasChallengeSetup) {
                 console.log("[cancelPreparationLogic] Resetting active challenge state due to preparation cancellation.");
                 setChallengeState({ isActive: false, targetNBack: null, wins: 0 });
            }
            console.log('[cancelPreparationLogic] State reset to idle.');
        } else {
             console.debug('[cancelPreparationLogic] No action taken, state is not "preparing".');
        }
    };

    return {
        startGameLogic,
        startPlaygroundGameLogic,
        cancelPreparationLogic,
    };
}